import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  guideAvailabilityApi,
  guideAvailabilityKeys,
  type AvailabilityListParams,
  type BlockAvailabilityRangePayload,
  type CreateAvailabilityPayload,
  type UpdateAvailabilityPayload,
} from '@/features/guide/api/guide-availability.api';

export function useGuideBlockedDates(params?: AvailabilityListParams) {
  return useQuery({
    queryKey: guideAvailabilityKeys.range(params),
    queryFn: () => guideAvailabilityApi.list(params ?? {}),
  });
}

/** Booked time for the next two months (the schedule endpoint's maximum range). */
export function useGuideBookedDays() {
  const from = new Date().toISOString().slice(0, 10);
  const toDate = new Date();
  toDate.setDate(toDate.getDate() + 61);
  const params = { from, to: toDate.toISOString().slice(0, 10) };
  return useQuery({
    queryKey: guideAvailabilityKeys.schedule(params),
    queryFn: () => guideAvailabilityApi.getSchedule(params),
    select: (schedule) => schedule.days.filter((day) => day.busy.length > 0),
  });
}

export function useCreateAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAvailabilityPayload) => guideAvailabilityApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideAvailabilityKeys.all });
    },
  });
}

export function useUpdateAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAvailabilityPayload }) =>
      guideAvailabilityApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideAvailabilityKeys.all });
    },
  });
}

export function useBlockAvailabilityRange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BlockAvailabilityRangePayload) => guideAvailabilityApi.blockRange(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideAvailabilityKeys.all });
    },
  });
}

export function useDeleteAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => guideAvailabilityApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guideAvailabilityKeys.all });
    },
  });
}
